import api from './api';
import { isValidObjectId } from '../utils/mongoUtils';

export interface AssetPhoto {
  _id?: string;
  url: string;
  description?: string;
  uploadDate?: string;
}

export interface AssetDocument {
  _id?: string;
  url: string;
  name: string;
  type?: string;
  uploadDate?: string;
}

export interface AssetAddress {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

// Interface para o cliente quando retornado como objeto
export interface ClientReference {
  _id: string;
  name: string;
  documentNumber?: string;
  documentType?: string;
  [key: string]: any; // Para outros campos que possam existir
}

export interface Asset {
  _id?: string;
  name: string;
  description?: string;
  category: string;
  client: string | ClientReference; // Pode ser um ID (string) ou um objeto cliente populado
  serialNumber?: string;
  model?: string;
  manufacturer?: string;
  purchaseDate?: string;
  purchaseValue?: number;
  status: 'active' | 'maintenance' | 'inactive' | 'retired';
  address?: AssetAddress;
  locationDescription?: string;
  photos: AssetPhoto[];
  documents: AssetDocument[];
  notes?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssetsResponse {
  assets: Asset[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface AssetParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  client?: string;
  status?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const assetService = {
  // Get all assets with pagination and filters
  getAssets: async (params: AssetParams = {}): Promise<AssetsResponse> => {
    const response = await api.get('/assets', { params });
    return response.data;
  },

  // Get a single asset by ID
  getAssetById: async (assetId: string): Promise<{ asset: Asset | null; error?: string }> => {
    console.log('[AssetService] getAssetById:', assetId);
    
    // Verificar se o ID é um ObjectId válido do MongoDB
    if (!assetId || !isValidObjectId(assetId)) {
      console.warn(`ID de ativo inválido: ${assetId}`);
      return { 
        asset: null, 
        error: `ID de ativo inválido: ${assetId}. Não é um ObjectId válido do MongoDB.` 
      };
    }
    
    try {
      const response = await api.get(`/assets/${assetId}`);
      console.log('[AssetService] Asset carregado:', response.data.asset?.name);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao buscar ativo:`, error.response?.data?.message || error.message);
      return { 
        asset: null, 
        error: error.response?.data?.message || `Erro ao buscar ativo com ID ${assetId}` 
      };
    }
  },

  // Create a new asset
  createAsset: async (assetData: Omit<Asset, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const response = await api.post('/assets', assetData);
      // Adicionar flag de sucesso para facilitar a verificação no componente
      return {
        ...response.data,
        success: true
      };
    } catch (error: any) {
      console.error('Erro ao criar ativo:', error);
      
      // Log detalhado da estrutura de erro para depuração
      console.error('Estrutura completa do erro:', {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Extrair mensagem de erro do backend em diferentes formatos possíveis
      let errorMessage = 'Erro ao criar ativo. Por favor, verifique os dados e tente novamente.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.details) {
          errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
        } else if (errorData.validation) {
          errorMessage = `Erro de validação: ${JSON.stringify(errorData.validation)}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Update an asset
  updateAsset: async (assetId: string, assetData: Partial<Asset>) => {
    try {
      const response = await api.put(`/assets/${assetId}`, assetData);
      // Adicionar flag de sucesso para facilitar a verificação no componente
      return {
        ...response.data,
        success: true
      };
    } catch (error: any) {
      console.error(`Erro ao atualizar ativo com ID ${assetId}:`, error);
      
      // Log detalhado da estrutura de erro para depuração
      console.error('Estrutura completa do erro:', {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Extrair mensagem de erro do backend em diferentes formatos possíveis
      let errorMessage = `Erro ao atualizar ativo. Por favor, verifique os dados e tente novamente.`;
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.details) {
          errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
        } else if (errorData.validation) {
          errorMessage = `Erro de validação: ${JSON.stringify(errorData.validation)}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Delete an asset
  deleteAsset: async (assetId: string) => {
    try {
      const response = await api.delete(`/assets/${assetId}`);
      // Adicionar flag de sucesso para facilitar a verificação no componente
      return {
        ...response.data,
        success: true
      };
    } catch (error: any) {
      console.error(`Erro ao excluir ativo com ID ${assetId}:`, error);
      
      // Log detalhado da estrutura de erro para depuração
      console.error('Estrutura completa do erro:', {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Extrair mensagem de erro do backend em diferentes formatos possíveis
      let errorMessage = `Erro ao excluir ativo. Por favor, tente novamente.`;
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.details) {
          errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
        } else if (errorData.validation) {
          errorMessage = `Erro de validação: ${JSON.stringify(errorData.validation)}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Upload asset photo
  uploadAssetPhoto: async (assetId: string, photoFile: File, description?: string) => {
    const formData = new FormData();
    formData.append('photo', photoFile);
    
    if (description) {
      formData.append('description', description);
    }
    
    const response = await api.post(`/assets/${assetId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  },

  // Remove asset photo
  removeAssetPhoto: async (assetId: string, photoId: string) => {
    const response = await api.delete(`/assets/${assetId}/photos/${photoId}`);
    return response.data;
  }
};

export default assetService;
